import { visit } from 'unist-util-visit';
import type { Blockquote, Paragraph, PhrasingContent, Root, Text } from 'mdast';
import type { Properties } from 'hast';

const KNOWN_TYPES = ['note', 'tip', 'important', 'warning', 'caution'] as const;

const MARKER = /^\[!([A-Za-z][A-Za-z0-9_-]*)\]\s*(.*)$/;

interface HProps {
  hName?: string;
  hProperties?: Properties;
}

/**
 * Rewrites GitHub alert blockquotes (`> [!NOTE]`) into `div[data-callout]`
 * elements that the renderer maps onto the `Callout` component.
 *
 * Unknown types are preserved verbatim and rendered with neutral styling rather
 * than being dropped, so `> [!SOMETHING]` still shows its content.
 */
export default function remarkAlerts() {
  return (tree: Root) => {
    visit(tree, 'blockquote', (node: Blockquote) => {
      const first = node.children[0];
      if (!first || first.type !== 'paragraph') return;

      const paragraph = first as Paragraph;
      const firstChild = paragraph.children[0];
      if (!firstChild || firstChild.type !== 'text') return;

      const textNode = firstChild as Text;
      const newlineIndex = textNode.value.indexOf('\n');
      const firstLine = newlineIndex === -1 ? textNode.value : textNode.value.slice(0, newlineIndex);
      const match = MARKER.exec(firstLine.trim());
      if (!match) return;

      const rawType = match[1].toLowerCase();
      const title = match[2].trim();

      const remainder = newlineIndex === -1 ? '' : textNode.value.slice(newlineIndex + 1);
      if (remainder === '') {
        paragraph.children.shift();
      } else {
        textNode.value = remainder;
      }

      // The marker may have been the paragraph's only content.
      if (paragraph.children.length === 0) {
        node.children.shift();
      }

      const data = (node.data ??= {}) as HProps;
      data.hName = 'div';
      data.hProperties = {
        className: ['callout', `callout-${KNOWN_TYPES.includes(rawType as never) ? rawType : 'default'}`],
        dataCallout: rawType,
        ...(title ? { dataCalloutTitle: title } : {}),
      };
    });
  };
}

export const isKnownCalloutType = (type: string): boolean =>
  KNOWN_TYPES.includes(type.toLowerCase() as never);

export type { PhrasingContent };
